import { useContext, useMemo } from 'react';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';

export type NavScreenOption = {
    label: string;
    path: string;
    id?: string;
    isIframe?: boolean;
    // Absolute route resolved from `screenDetailsWithRoutes`; see `resolveOptionUrls`
    url?: string;
};

export type NavScreenGroup = {
    label: string;
    path: (string | null)[];
    showSubHeader?: boolean;
    /** Stable key for surfaces that address a group directly (AI Intelligent Studio). */
    id?: string;
    /** Registry key for the group's card artwork; see constants/aiStudio.ts. */
    icon?: string;
    /** One-line card description. */
    description?: string;
    /**
     * Absolute route for a group that is itself a destination — an admin-created top-level
     * embedded route, which has no children until one is nested under it.
     */
    url?: string;
    options: Record<string, NavScreenOption[]>;
};

// Column keys in declaration order, minus the `visible` sentinel every tenant config carries.
// The surviving order is what indexes into `group.path` — the same convention NavBarOptions
// relies on when it reads `path[0]` for Promo and `path[1]` for Pricing.
export const getColumnKeys = (group: NavScreenGroup): string[] =>
    Object.keys(group?.options || {}).filter((key) => Array.isArray(group.options[key]));

const readUserAccessScopeScreens = (): string[] | undefined => {
    try {
        const userScope = JSON.parse(localStorage.getItem('userAccessScope') || '{}');
        return userScope?.data_metrics?.forecast_sources?.screens;
    } catch {
        return undefined;
    }
};

// One whitelist pass over the `screens` config. An absent or empty list means "no restriction".
// `isIframe` options are config-driven demo screens with no id in any access scope, so they
// bypass the whitelist — same exemption Navbar.tsx applies.
const filterGroupsByAccessibility = (groups: NavScreenGroup[], accessibleScreens?: string[]): NavScreenGroup[] => {
    if (!Array.isArray(accessibleScreens) || accessibleScreens.length === 0) return groups;

    return groups.reduce<NavScreenGroup[]>((kept, group) => {
        const options: Record<string, NavScreenOption[]> = {};

        Object.keys(group?.options || {}).forEach((key) => {
            const entries = group.options[key];
            if (!Array.isArray(entries)) return;
            const allowed = entries.filter((entry) => accessibleScreens.includes(entry?.id || '') || entry?.isIframe);
            if (allowed.length) options[key] = allowed;
        });

        // A group with no surviving children has nothing left to navigate to — unless it is a
        // destination in its own right, which a top-level admin-created route is.
        if (Object.keys(options).length || group?.url) kept.push({ ...group, options });
        return kept;
    }, []);
};

// `screens[].path` is positional — an option column indexes into it by its position among the
// group's columns (Promo -> path[0], Pricing -> path[1]). A group that declares only the later
// column still carries the earlier prefix in `path`, so deriving the URL from the surviving
// column's position sends e.g. the Pricing Simulator to `promo/recommendation/pricing-simulator`,
// which no route registers. `screenDetailsWithRoutes` is what the router actually registers, so
// resolve each option against it by id and leave the positional prefix as the only fallback.
const buildRouteUrlLookup = (screenDetails?: any[]): Record<string, string> => {
    const lookup: Record<string, string> = {};
    if (!Array.isArray(screenDetails)) return lookup;

    screenDetails.forEach((screen) => {
        const path = screen?.path;
        // Parameterised routes (`.../:mode/:projectId`) are never a nav destination
        if (typeof path !== 'string' || !path || !screen?.id || path.includes(':')) return;
        // Several routes share one id (`psr` sits on both the simulator and its `/new` form).
        // The shortest is the landing route, so the winner does not depend on config order.
        const url = `/${path}`;
        if (!lookup[screen.id] || url.length < lookup[screen.id].length) lookup[screen.id] = url;
    });

    return lookup;
};

const resolveOptionUrls = (groups: NavScreenGroup[], screenDetails?: any[]): NavScreenGroup[] => {
    const lookup = buildRouteUrlLookup(screenDetails);
    if (!Object.keys(lookup).length) return groups;

    return groups.map((group) => {
        const options: Record<string, NavScreenOption[]> = {};

        Object.keys(group?.options || {}).forEach((key) => {
            const entries = group.options[key];
            if (!Array.isArray(entries)) return;
            options[key] = entries.map((entry) => {
                const url = lookup[entry?.id || ''];
                return url ? { ...entry, url } : entry;
            });
        });

        return { ...group, options };
    });
};

/**
 * Navigation tree for Navbar V2, filtered by user access scope and then by the tenant's
 * `commonConfig.accessibleScreens`.
 *
 * Deliberately not a copy of `Navbar.tsx#filterScreensByUserRoleAccessAndTenantSettings`:
 * that version tests `accessbileScreens?.lenght === 0` (typo, so the documented "empty means
 * everything" escape hatch never fires) and rebuilds each group as `{ label, path, options }`,
 * dropping `showSubHeader`. V2 renders sub-headers from that flag, so it has to survive.
 */
export const useAccessibleNavScreens = (): NavScreenGroup[] => {
    const { appConfig } = useContext(UserContext);

    return useMemo(() => {
        const screens: NavScreenGroup[] = getConfig('screens', appConfig) || [];
        if (!Array.isArray(screens)) return [];

        const byUserScope = filterGroupsByAccessibility(screens, readUserAccessScopeScreens());
        const accessible = filterGroupsByAccessibility(
            byUserScope,
            getConfig('commonConfig', appConfig)?.accessibleScreens,
        );
        return resolveOptionUrls(accessible, getConfig('screenDetailsWithRoutes', appConfig));
    }, [appConfig]);
};

export default useAccessibleNavScreens;
